// SETTINGS V2 — Team row kebab menu (SETTINGS_V2_SP6 §5.4). Radix DropdownMenu
// with the SP3/SP4 portal scope-class trick. Permanent vs temporary action sets
// (temporary flags come from the imported legacy getButtonStates); Resend is
// disabled-when-joined; delete lives inside PermissionGuard(Users, Admin) — the
// ONLY frontend-guarded action (parity). NO change-role item (dead path stays
// dead). The trigger stops propagation so a phone card-tap does NOT fire the
// menu, and a kebab-tap does NOT toggle the card detail. Inline styles + scoped
// CSS-var tokens + Radix. No MUI/sx/dt/Tailwind. SEC-REQ-2: no side effects.
import React from "react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";

import { useAppSelector } from "../../../../shared/hooks/sharedHooks";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { useBreakpoint } from "../../../../shared/hooks/useBreakpoint";
import PermissionGuard from "../../../../shared/components/PermissionGuard";
import { Modules, PermissionLevel } from "../../../../core/models/PermissionModels";
import { User, AccessTypeCode } from "../../models/userModels";
import { getButtonStates } from "../../utils/employeeUtils";

export interface TeamRowV2MenuProps {
  user: User;
  onReinvite: (user: User) => void;
  onPinRegenerate: (user: User) => void;
  onDisable: (user: User) => void;
  onEnable: (user: User) => void;
  onDelete: (user: User) => void;
}

const TeamRowV2Menu: React.FC<TeamRowV2MenuProps> = ({
  user,
  onReinvite,
  onPinRegenerate,
  onDisable,
  onEnable,
  onDelete,
}) => {
  const { translate: t } = useTranslation("settings");
  const theme = useAppSelector((s) => s.settingsV2Ui.theme);
  const { isPhone } = useBreakpoint();

  const isPermanent = user.accessType === AccessTypeCode.permanent;
  const bs = getButtonStates(user);

  const size = isPhone ? 44 : 28;

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          className="btn btn-ghost"
          aria-label={t("settings:actions") as string}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          style={{
            width: size,
            minWidth: size,
            height: size,
            padding: 0,
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          ⋮
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          className={`settings-v2-root p-${theme} mod-nastavenia sv2-menu`}
          align="end"
          sideOffset={4}
          onClick={(e) => e.stopPropagation()}
        >
          {isPermanent ? (
            <>
              <DropdownMenu.Item
                className="sv2-menu-item"
                disabled={!!user.loginDate}
                onSelect={() => onReinvite(user)}
              >
                {t("settings:resend") as string}
              </DropdownMenu.Item>
              <PermissionGuard module={Modules.Users} level={PermissionLevel.Admin}>
                <DropdownMenu.Item
                  className="sv2-menu-item"
                  onSelect={() => onDelete(user)}
                  style={{ color: "var(--red)" }}
                >
                  {t("common:buttons.delete") as string}
                </DropdownMenu.Item>
              </PermissionGuard>
            </>
          ) : (
            <>
              {bs.regeneratePin && (
                <DropdownMenu.Item
                  className="sv2-menu-item"
                  disabled={bs.regeneratePinDisabled}
                  onSelect={() => onPinRegenerate(user)}
                >
                  {t("settings:regeneratePIN") as string}
                </DropdownMenu.Item>
              )}
              {bs.disable && (
                <DropdownMenu.Item
                  className="sv2-menu-item"
                  onSelect={() => onDisable(user)}
                >
                  {t("settings:disable") as string}
                </DropdownMenu.Item>
              )}
              {bs.activate && (
                <DropdownMenu.Item
                  className="sv2-menu-item"
                  onSelect={() => onEnable(user)}
                >
                  {t("settings:activate") as string}
                </DropdownMenu.Item>
              )}
              {bs.delete && (
                <PermissionGuard module={Modules.Users} level={PermissionLevel.Admin}>
                  <DropdownMenu.Item
                    className="sv2-menu-item"
                    onSelect={() => onDelete(user)}
                    style={{ color: "var(--red)" }}
                  >
                    {t("common:buttons.delete") as string}
                  </DropdownMenu.Item>
                </PermissionGuard>
              )}
            </>
          )}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
};

export default TeamRowV2Menu;
